// Итоги «Рекламы» за период и изменение к прошлому периоду той же длины (у «сегодня» — ко вчера в это же
// время, у «этого месяца» — к тем же дням прошлого). Проценты, п.п. и разницу считает api; апрувы прошлого
// периода — «на тот момент»; здесь — стрелка, цвет по смыслу и подпись, с чем сравнили.
import { type Period, periodLabel } from '@autoaff/schemas/period';
import { int, pct, Skeleton, signedUsd, usd } from '@autoaff/ui';
import clsx from 'clsx';
import { ArrowDown, ArrowUp } from 'lucide-react';
import type { ReactNode } from 'react';
import type { AdsCompare, AdsData, ChangeKey } from '../../business';
import { tone } from './columns';

/** С чем сравнили: «к прошлым 7 дням» — для строки изменения; «прошлые 7 дней» — для подсказки и нуля. */
export function compareWords(period: Period, c: AdsCompare): { to: string; name: string } {
  if (c.kind === 'same_time') return { to: 'к вчера в это время', name: 'вчера к этому часу' };
  if (period.kind === 'preset')
    switch (period.preset) {
      case 'yesterday':
        return { to: 'к позавчера', name: 'позавчера' };
      case '7d':
        return { to: 'к прошлым 7 дням', name: 'прошлые 7 дней' };
      case '14d':
        return { to: 'к прошлым 14 дням', name: 'прошлые 14 дней' };
      case '30d':
        return { to: 'к прошлым 30 дням', name: 'прошлые 30 дней' };
      case 'prev_month':
        return { to: 'к позапрошлому месяцу', name: 'позапрошлый месяц' };
      default:
        // «Этот месяц» — к тем же дням прошлого: «к 1–26 авг»
        break;
    }
  const label = periodLabel({ kind: 'range', since: c.since, until: c.until });
  return { to: `к ${label}`, name: label };
}

type Good = 'up' | 'down' | null;

/**
 * Строка изменения: «▲ 12.4% к прошлым 7 дням». Цвет — только где направление однозначно: для лидов рост
 * хорошо, для цены лида и апрува — плохо; расход — без цвета (рост бывает и масштабом).
 */
function Change({
  value,
  money,
  good,
  words,
  before,
}: {
  /** Изменение в процентах (или в деньгах, если money); null — сравнить не с чем. */
  value: number | null;
  money?: boolean;
  good: Good;
  words: { to: string; name: string };
  /** Прошлое значение, как его показать; null — неизвестно (нет статистики за те дни). */
  before: string | null;
}) {
  if (before === null) return <small className="kpi-change">{words.name}: нет данных</small>;
  if (value === null) return <small className="kpi-change">{`${words.name}: ${before}`}</small>;
  const shown = money ? usd(Math.abs(value)) : pct(Math.abs(value));
  const zero = money ? Math.abs(value) < 0.005 : Math.abs(value) < 0.05;
  const up = value > 0;
  const Icon = up ? ArrowUp : ArrowDown;
  const cls = zero || good === null ? undefined : (good === 'up') === up ? 'pos' : 'neg';
  return (
    <small className="kpi-change" title={`${words.name[0]?.toUpperCase()}${words.name.slice(1)}: ${before}`}>
      <span className={clsx('delta', cls)}>
        {!zero && <Icon aria-hidden="true" />}
        {!zero && <span className="sr-only">{up ? 'рост на ' : 'снижение на '}</span>}
        {shown}
      </span>{' '}
      <span className="kpi-vs">{words.to}</span>
    </small>
  );
}

function Tile({
  label,
  value,
  valueTone,
  change,
  sub,
}: {
  label: string;
  value: string;
  valueTone?: string;
  change?: ReactNode;
  sub: ReactNode;
}) {
  return (
    <div className="kpi">
      <span>{label}</span>
      <b className={valueTone}>{value}</b>
      {change}
      <small>{sub}</small>
    </div>
  );
}

export function AdsKpis({ d, period }: { d: AdsData | undefined; period: Period }) {
  if (!d)
    return (
      <section className="kpis ads-kpis" aria-label="Итоги рекламы">
        {[0, 1, 2, 3, 4].map((i) => (
          <div key={i} className="kpi">
            <Skeleton w="50%" />
            <Skeleton w="70%" h={26} />
            <Skeleton w="80%" h={12} />
          </div>
        ))}
      </section>
    );
  const t = d.totals;
  const c = d.compare;
  const words = c ? compareWords(period, c) : null;
  /** Изменение метрики в процентах; before — прошлое значение словами. */
  const change = (k: ChangeKey, good: Good, fmt: (n: number) => string) => {
    if (!c || !words) return undefined;
    const prev = c.totals[k];
    const spendBased = !['leads', 'approved', 'approveRate', 'revenue'].includes(k);
    const before = spendBased && !c.spendKnown ? null : prev === null ? '—' : fmt(prev);
    return <Change value={c.change[k]} good={good} words={words} before={before} />;
  };
  const moneyChange = (k: 'profit' | 'forecastProfit') => {
    if (!c || !words) return undefined;
    return (
      <Change
        value={c.diff[k]}
        money
        good="up"
        words={words}
        before={c.diff[k] === null ? null : signedUsd(c.totals[k])}
      />
    );
  };

  return (
    <section className="kpis ads-kpis" aria-label="Итоги рекламы">
      <Tile
        label="Расход"
        value={usd(t.spend)}
        change={change('spend', null, (n) => usd(n))}
        sub={`${int(t.impressions)} показов${t.ctr !== null ? ` · CTR ${pct(t.ctr)}` : ''}`}
      />
      <Tile
        label="Лиды"
        value={int(t.leads)}
        change={change('leads', 'up', int)}
        sub={t.cpl !== null ? <CplLine d={d} /> : 'пока без лидов'}
      />
      <Tile
        label="Апрувы"
        value={int(t.approved)}
        change={change('approved', 'up', int)}
        sub={<ApproveLine d={d} />}
      />
      <Tile
        label="Прибыль"
        value={signedUsd(t.profit)}
        valueTone={tone(t.profit)}
        change={moneyChange('profit')}
        sub={`выплаты ${usd(t.revenue)}`}
      />
      <Tile
        label="Прогноз прибыли"
        value={signedUsd(t.forecastProfit)}
        valueTone={tone(t.forecastProfit)}
        change={moneyChange('forecastProfit')}
        sub={
          t.inProcess > 0
            ? `+${usd(t.expected)} ждём от ${int(t.inProcess)} в обработке`
            : 'все лиды с решением'
        }
      />
    </section>
  );
}

/**
 * «CPL $2.91 ▼ 5.1% · окупаемый $4.20» — цена лида и её изменение (рост цены — плохо); при фильтре по офферу —
 * рядом окупаемый CPL оффера.
 */
function CplLine({ d }: { d: AdsData }) {
  const cpl = d.totals.cpl;
  const ch = d.compare?.change.cpl ?? null;
  if (cpl === null) return null;
  const be = d.totals.breakEvenCpl ?? null;
  const tail = be !== null ? ` · окупаемый ${usd(be)}` : '';
  if (ch === null || Math.abs(ch) < 0.05)
    return (
      <>
        CPL {usd(cpl)}
        {tail}
      </>
    );
  const Icon = ch > 0 ? ArrowUp : ArrowDown;
  return (
    <>
      CPL {usd(cpl)}{' '}
      <span className={clsx('delta', ch > 0 ? 'neg' : 'pos')}>
        <Icon aria-hidden="true" />
        <span className="sr-only">{ch > 0 ? 'дороже на ' : 'дешевле на '}</span>
        {pct(Math.abs(ch))}
      </span>
      {tail}
    </>
  );
}

/**
 * «42.0% от решённых ▼ 1.5 п.п.», строкой ниже «$8.33 за апрув» — % апрува (одна формула платформы) и его
 * изменение в процентных пунктах: падение — плохо.
 */
function ApproveLine({ d }: { d: AdsData }) {
  const t = d.totals;
  const cost = t.approveCost !== null ? `${usd(t.approveCost)} за апрув` : null;
  if (t.approveRate === null) return <>{cost ?? 'пока без решений сети'}</>;
  const ch = d.compare?.change.approveRate ?? null;
  const Icon = ch !== null && ch > 0 ? ArrowUp : ArrowDown;
  return (
    <>
      {pct(t.approveRate)} от решённых
      {ch !== null && Math.abs(ch) >= 0.05 && (
        <>
          {' '}
          <span className={clsx('delta', ch > 0 ? 'pos' : 'neg')}>
            <Icon aria-hidden="true" />
            <span className="sr-only">{ch > 0 ? 'выше на ' : 'ниже на '}</span>
            {Math.abs(ch).toFixed(1)} п.п.
          </span>
        </>
      )}
      {cost && (
        <>
          <br />
          {cost}
        </>
      )}
    </>
  );
}
